import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "@/lib/auth";

const ThemeContext = createContext(null);

function readTheme(storageKey) {
  if (!storageKey) return "dark";
  try { return window.localStorage.getItem(storageKey) === "light" ? "light" : "dark"; }
  catch { return "dark"; }
}

export const THEME_LABELS = {
  en: { title: "Interface theme", light: "Light", dark: "Dark", help: "Saved for this account in this browser." },
  it: { title: "Tema dell’interfaccia", light: "Chiaro", dark: "Scuro", help: "Salvato per questo account in questo browser." },
  de: { title: "Oberflächendesign", light: "Hell", dark: "Dunkel", help: "Für dieses Konto in diesem Browser gespeichert." },
  es: { title: "Tema de la interfaz", light: "Claro", dark: "Oscuro", help: "Guardado para esta cuenta en este navegador." },
  zh: { title: "界面主题", light: "浅色", dark: "深色", help: "在此浏览器中为此账户保存。" },
  ru: { title: "Тема интерфейса", light: "Светлая", dark: "Тёмная", help: "Сохраняется для этого аккаунта в этом браузере." },
};

export function WorkspaceThemeProvider({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isWorkspace = pathname === "/app" || pathname.startsWith("/app/");
  const storageKey = isWorkspace ? (user?.id ? `td_workspace_theme:${user.id}` : null) : "td_public_theme";
  const [preference, setPreference] = useState(() => ({ storageKey, theme: readTheme(storageKey) }));
  const selectedTheme = preference.storageKey === storageKey ? preference.theme : readTheme(storageKey);
  const theme = selectedTheme;

  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-workspace-theme", theme);
    return () => document.documentElement.removeAttribute("data-workspace-theme");
  }, [theme]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.storageArea === window.localStorage && (event.key === storageKey || event.key === null)) {
        setPreference({ storageKey, theme: readTheme(storageKey) });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  const setTheme = (nextTheme) => {
    if (!storageKey || !["light", "dark"].includes(nextTheme)) return;
    setPreference({ storageKey, theme: nextTheme });
    try { window.localStorage.setItem(storageKey, nextTheme); }
    catch { /* Theme switching remains usable when browser storage is unavailable. */ }
  };

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useWorkspaceTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useWorkspaceTheme requires WorkspaceThemeProvider");
  return context;
}

export function WorkspaceToaster() {
  const { theme } = useWorkspaceTheme();
  return <Toaster theme={theme} position="top-right" />;
}
